/**
 * @file: SettingRow.tsx
 *
 * A labelled toggle row in the room-detail settings block, plus the thin
 * inter-row separator. Presentational only.
 */

import { Typography } from '@euler/app/components/typography';
import { FontFamily } from '@euler/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { ChevronRight } from 'lucide-react-native';
import { FC, memo } from 'react';
import { StyleSheet, Switch, TouchableOpacity, View } from 'react-native';

export const SettingRow: FC<{
  label: string;
  value: boolean;
  onValueChange: (v: boolean) => void;
  disabled?: boolean;
}> = memo(({ label, value, onValueChange, disabled }) => {
  const { rpx } = useResponsiveContext();
  return (
    <View
      css={`
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
        padding: ${rpx(14)}px ${rpx(16)}px;
        opacity: ${disabled ? 0.5 : 1};
      `}
    >
      <Typography.Text
        size={rpx(15)}
        color="#111"
        style={{ fontFamily: FontFamily.NotoSans.Regular }}
      >
        {label}
      </Typography.Text>
      <Switch value={value} onValueChange={onValueChange} disabled={disabled} />
    </View>
  );
});

/** A tappable row showing a label + current value + chevron (e.g. rename). */
export const LinkRow: FC<{
  label: string;
  value?: string | null;
  onPress: () => void;
  disabled?: boolean;
}> = memo(({ label, value, onPress, disabled }) => {
  const { rpx } = useResponsiveContext();
  return (
    <TouchableOpacity
      onPress={onPress}
      disabled={disabled}
      activeOpacity={0.6}
      css={`
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
        padding: ${rpx(14)}px ${rpx(16)}px;
        opacity: ${disabled ? 0.5 : 1};
      `}
    >
      <Typography.Text
        size={rpx(15)}
        color="#111"
        style={{ fontFamily: FontFamily.NotoSans.Regular }}
      >
        {label}
      </Typography.Text>
      <View
        css={`
          flex-direction: row;
          align-items: center;
          flex-shrink: 1;
          margin-left: ${rpx(12)}px;
        `}
      >
        <Typography.Text
          size={rpx(14)}
          color="#9ca3af"
          numberOfLines={1}
          style={{ flexShrink: 1, textAlign: 'right' }}
        >
          {value || ''}
        </Typography.Text>
        <ChevronRight size={rpx(16)} color="#c4c4c4" />
      </View>
    </TouchableOpacity>
  );
});

export const SettingSeparator = memo(() => (
  <View
    style={{
      height: StyleSheet.hairlineWidth,
      backgroundColor: '#f0f0f0',
      marginLeft: 16,
    }}
  />
));
